/**
 * Execute skill insertion plans in the open Word document.
 */
import {
  applyHeadingStructureToInsertedText,
  insertAtMultipleAgentAnchors,
  moveSkillInsertions,
  type SkillReposition,
} from '../../../word/api/AgentDocumentInserter';
import { DocumentReader } from '../../../word/api/DocumentReader';
import { TrackChangesManager } from '../../../word/trackChanges/TrackChangesManager';
import { log, logError } from '../../../utils/logging';
import type { SkillInsertionPlan, SkillRepositionMove } from './types';

export async function executeSkillInsertions(
  insertions: SkillInsertionPlan[],
  /** Text of a genuine EXISTING heading in the document — a brand-new heading
   * in this batch (paragraph_role: "heading") copies ITS paragraph style and
   * list membership/level, not whatever paragraph happens to be adjacent in
   * this insertion run (see applyHeadingStructureToInsertedText's docstring
   * for why that generic proximity copy silently mis-styles a new heading as
   * a regular numbered clause). Required whenever `insertions` contains a
   * heading item; a heading item with no reference is left as inserted by
   * the generic batch path (bold-only, list membership unverified). */
  headingReferenceText?: string,
): Promise<{ succeeded: number; failed: number; details: string[] }> {
  const needsTrackChanges = insertions.some((ins) => ins.format === 'track_changes');
  if (needsTrackChanges) {
    const enabled = await TrackChangesManager.ensureEnabled();
    if (!enabled) {
      logError('[SkillWordExecutor] track changes could not be enabled');
      return {
        succeeded: 0,
        failed: insertions.length,
        details: insertions.map(
          (_, i) => `Insertion ${i + 1}: track changes unavailable in this Word host`,
        ),
      };
    }
    log('[SkillWordExecutor] track changes enabled for skill insertions');
  }

  const batchItems = insertions.map((ins) => ({
    text: ins.client_request_text,
    afterAgentParagraphIndex: ins.target_paragraph_index,
    trackChangesAuthor:
      ins.format === 'track_changes' ? (ins.author ?? 'Unknown').trim() || 'Unknown' : undefined,
  }));

  const result = await insertAtMultipleAgentAnchors(batchItems);
  log('[SkillWordExecutor] complete', {
    succeeded: result.succeeded,
    failed: result.failed,
  });

  const headingTexts = insertions
    .filter((ins) => ins.paragraph_role === 'heading')
    .map((ins) => ins.client_request_text);
  if (headingTexts.length > 0 && result.succeeded > 0 && headingReferenceText) {
    await applyHeadingStructureToInsertedText(headingTexts, headingReferenceText);
  }

  return {
    succeeded: result.succeeded,
    failed: result.failed,
    details: result.details,
  };
}

export async function getDocumentParagraphsForSkill(): Promise<
  Array<{ index: number; text: string }>
> {
  const { paragraphs } = await DocumentReader.getFullDocumentContext();
  // AgentDocumentInserter maps target_paragraph_index to the Nth non-empty body
  // paragraph (0..n-1). DocumentReader keeps Office body.paragraphs indices which
  // include gaps for empty paragraphs — re-number so engine plans match Word insert.
  return paragraphs.map((p, sequentialIndex) => ({
    index: sequentialIndex,
    text: p.text,
  }));
}

/** Post-execute self-review follow-up: move a just-installed client request to a
 * different paragraph (exact-text match against what was just inserted, then
 * insert-at-new-anchor + delete-old, see AgentDocumentInserter.moveSkillInsertions). */
export async function repositionSkillInsertions(
  moves: SkillRepositionMove[],
): Promise<{ succeeded: number; failed: number; details: string[] }> {
  const payload: SkillReposition[] = moves.map((m) => ({
    matchText: m.match_text,
    targetAgentParagraphIndex: m.target_paragraph_index,
    trackChangesAuthor: m.format === 'track_changes' ? (m.author ?? 'Unknown').trim() || 'Unknown' : undefined,
  }));
  const result = await moveSkillInsertions(payload);
  log('[SkillWordExecutor] reposition complete', { succeeded: result.succeeded, failed: result.failed });
  return result;
}
